import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    MagnifyingGlass,
    Cloud,
    ArrowsClockwise,
    Warning,
    Sliders,
    Sparkle,
    Prohibit,
    Target as TargetIcon,
    Download,
    Trash,
    CaretDown,
    CaretRight,
    X,
    Info,
    MapPin,
    RocketLaunch,
    UploadSimple,
    ArrowFatDown,
} from "@phosphor-icons/react";
import {
    adsStatus,
    adsListAccounts,
    keywordPlannerOptions,
    keywordPlannerIdeas,
    keywordPlannerIdeasDFS,
    keywordPlannerGeoSuggest,
    campaignBuilderPushNegatives,
    historyRelatedLookup, errText,
} from "@/lib/api";
import { CampaignPushWizard } from "@/components/CampaignPushWizard";
import { AdClipboardStrip } from "@/components/AdClipboardStrip";
import { KeywordDistillerDialog } from "@/components/KeywordDistillerDialog";
import { KeywordThemer } from "@/components/KeywordThemer";

const NETWORKS = [
    { key: "google_search", label: "Search only" },
    { key: "google_search_and_partners", label: "Search + Partners" },
];

// ----- Utilities -----
const parseChips = (text) =>
    text
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);

const csvDownload = (filename, rows) => {
    if (!rows.length) {
        toast.error("Nothing to export");
        return;
    }
    const cols = Object.keys(rows[0]);
    const escape = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(",")].concat(
        rows.map((r) => cols.map((c) => escape(r[c])).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const isBrandedIdea = (idea, brandTerms, brandGroupNames) => {
    if (!idea) return false;
    const kw = (idea.keyword || "").toLowerCase();
    if (brandTerms.some((b) => b && kw.includes(b))) return true;
    if (!brandGroupNames.size) return false;
    return (idea.concepts || []).some(
        (c) => (c.type || "").toUpperCase() === "BRAND" || brandGroupNames.has(c.group)
    );
};

// ----- Component -----
export const KeywordPlanner = ({ pendingDistiller, onAuditSaved } = {}) => {
    // Connection state
    const [status, setStatus] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [accountsLoading, setAccountsLoading] = useState(false);

    // Option lists
    const [options, setOptions] = useState({ countries: [], languages: [] });

    // Inputs
    const [seedText, setSeedText] = useState("");
    const [pageUrl, setPageUrl] = useState("");
    const [country, setCountry] = useState("GB");
    const [language, setLanguage] = useState("en");
    const [network, setNetwork] = useState("google_search");
    const [brandTerms, setBrandTerms] = useState(""); // comma separated
    const [loading, setLoading] = useState(false);

    // City / geo picker
    const [geoQuery, setGeoQuery] = useState("");
    const [geoSuggestions, setGeoSuggestions] = useState([]);
    const [geoSuggestOpen, setGeoSuggestOpen] = useState(false);
    const [geoLoading, setGeoLoading] = useState(false);
    const [selectedGeos, setSelectedGeos] = useState([]); // [{id, name, canonical_name, target_type}]

    // Results
    const [result, setResult] = useState(null); // { ideas, concept_groups, seeds, ... }
    const [excludedGroups, setExcludedGroups] = useState(new Set()); // refine keywords panel
    const [selection, setSelection] = useState({}); // keyword -> "target" | "negative"
    const [tab, setTab] = useState("non_branded"); // "non_branded" | "branded"
    const [sortBy, setSortBy] = useState("searches"); // searches | competition | keyword | high_bid
    const [sortDir, setSortDir] = useState("desc");
    const [query, setQuery] = useState("");

    // Unified-table filters (source / volume / competition)
    const [sourceFilter, setSourceFilter] = useState(
        () => new Set(["Planner", "Related", "Competitor"])
    );
    const [minSearches, setMinSearches] = useState(0);
    const [compFilter, setCompFilter] = useState(
        () => new Set(["LOW", "MEDIUM", "HIGH", "UNSPECIFIED"])
    );

    // Campaign push wizard
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardOverride, setWizardOverride] = useState(null); // { targets, negatives }
    const [pushingNegs, setPushingNegs] = useState(false);

    // Three-bucket distiller
    const [distillerOpen, setDistillerOpen] = useState(false);
    const [preloadedDistiller, setPreloadedDistiller] = useState(null);

    // SERP-related-searches import
    const [serpImporting, setSerpImporting] = useState(false);
    const [serpImported, setSerpImported] = useState([]); // Array<string>

    // Pull related searches from any recent SERP research runs that match
    // (seed contains / is contained by the record's query) + city.
    const handleImportRelated = async () => {
        const primarySeed = seedText.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean)[0] || "";
        if (!primarySeed) {
            toast.error("Type at least one seed keyword first");
            return;
        }
        setSerpImporting(true);
        const t = toast.loading(`Looking up SERP related searches for "${primarySeed}"…`);
        try {
            const data = await historyRelatedLookup({
                seed: primarySeed,
                city: wizardCity,
                country,
                limit: 5,
            });
            const list = (data.related || []).filter(Boolean);
            if (!list.length) {
                toast.error("No matching Research runs found — run a SERP search first", { id: t });
                return;
            }
            // Prepend to seed text so the Distiller's master-list sees them.
            const existing = new Set(
                seedText
                    .split(/[\n,;]/)
                    .map((s) => s.trim().toLowerCase())
                    .filter(Boolean)
            );
            const fresh = list.filter((k) => !existing.has(k.toLowerCase()));
            if (fresh.length) {
                const joined = fresh.join("\n");
                setSeedText((prev) => (prev.trim() ? `${prev.trim()}\n${joined}` : joined));
            }
            setSerpImported((prev) => Array.from(new Set([...prev, ...list])));
            toast.success(
                `Imported ${fresh.length} new related searches from ${data.matched_records?.length || 0} SERP run${(data.matched_records?.length || 0) === 1 ? "" : "s"}`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Lookup failed");
            toast.error(msg, { id: t });
        } finally {
            setSerpImporting(false);
        }
    };

    // Auto-open the distiller with a persisted run when the user clicks a
    // "Distill · …" row in the sidebar. Also seed the input bar so the
    // context (seeds/brands/country/city) is right if they hit "Re-run".
    useEffect(() => {
        if (!pendingDistiller?.audit) return;
        const a = pendingDistiller.audit;
        setPreloadedDistiller({
            target: a.target || [],
            branded: a.branded || [],
            negatives: a.negatives || [],
            brand_terms_expanded: a.brand_terms_expanded || [],
            summary: a.summary || {},
            audit_id: a.id,
            created_at: a.created_at,
            label: a.label,
        });
        if (a.seed_text && !seedText) setSeedText(a.seed_text);
        if (a.brand_terms?.length) {
            setBrandTerms((a.brand_terms || []).join(", "));
        }
        setDistillerOpen(true);
    }, [pendingDistiller?.key]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Load status + options ----
    useEffect(() => {
        adsStatus().then(setStatus).catch(() => setStatus({ configured: false, connected: false }));
        keywordPlannerOptions()
            .then(setOptions)
            .catch(() => setOptions({ countries: [], languages: [] }));
    }, []);

    const loadAccounts = useCallback(async () => {
        setAccountsLoading(true);
        try {
            const data = await adsListAccounts();
            const list = data.accounts || [];
            setAccounts(list);
            if (list.length && !customerId) setCustomerId(list[0].id);
        } catch (e) {
            const msg = errText(e, "Failed to load accounts");
            toast.error(msg);
        } finally {
            setAccountsLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        if (status?.connected) loadAccounts();
    }, [status?.connected, loadAccounts]);

    // Debounced geo suggest — fires when the user types 2+ chars.
    useEffect(() => {
        const q = geoQuery.trim();
        if (q.length < 2) {
            setGeoSuggestions([]);
            return;
        }
        let cancelled = false;
        setGeoLoading(true);
        const t = setTimeout(async () => {
            try {
                const data = await keywordPlannerGeoSuggest(q, country, language);
                if (!cancelled) {
                    setGeoSuggestions(data.suggestions || []);
                    setGeoSuggestOpen(true);
                }
            } catch (e) {
                if (!cancelled) setGeoSuggestions([]);
            } finally {
                if (!cancelled) setGeoLoading(false);
            }
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [geoQuery, country, language]);

    const addGeo = (g) => {
        if (!g?.id) return;
        setSelectedGeos((prev) =>
            prev.some((p) => p.id === g.id) ? prev : [...prev, g]
        );
        setGeoQuery("");
        setGeoSuggestions([]);
        setGeoSuggestOpen(false);
    };

    const removeGeo = (id) =>
        setSelectedGeos((prev) => prev.filter((p) => p.id !== id));

    // ---- Fetch ideas ----
    const handleFetch = async () => {
        const seeds = parseChips(seedText);
        if (!seeds.length && !pageUrl.trim()) {
            toast.error("Enter at least one seed keyword or a page URL");
            return;
        }
        if (!customerId) {
            toast.error("Pick a client account first");
            return;
        }
        setLoading(true);
        const t = toast.loading("Fetching keyword ideas from Google Ads…");
        // Snapshot the three source buckets AT FETCH TIME so Source tagging
        // stays stable even if the user later edits the inputs. "Related"
        // takes priority over "Planner" if a term is both an imported SERP
        // related search AND a typed seed.
        const relatedSeedSet = new Set(
            (serpImported || [])
                .map((s) => (s || "").toLowerCase().trim())
                .filter(Boolean)
        );
        const userSeedSet = new Set(
            seeds
                .map((s) => s.toLowerCase().trim())
                .filter((s) => s && !relatedSeedSet.has(s))
        );
        const sourceMeta = {
            userSeeds: Array.from(userSeedSet),
            relatedSeeds: Array.from(relatedSeedSet),
            competitorUrl: pageUrl.trim(),
            hasCompetitor: !!pageUrl.trim(),
        };
        const basePayload = {
            seeds,
            page_url: pageUrl.trim(),
            country,
            language,
            limit: 500,
        };
        try {
            const data = await keywordPlannerIdeas({
                customer_id: customerId,
                ...basePayload,
                network,
                include_adult: false,
                extra_geo_ids: selectedGeos.map((g) => g.id),
            });
            setResult({ ...data, source: "google_ads", _sourceMeta: sourceMeta });
            setSelection({});
            setExcludedGroups(new Set());
            toast.success(
                `Got ${data.ideas?.length || 0} keyword ideas (Google Ads)`,
                { id: t }
            );
        } catch (e) {
            const detail = errText(e) || "";
            const shouldFallback =
                /developer token/i.test(detail) ||
                /not approved/i.test(detail) ||
                /service_disabled/i.test(detail) ||
                e?.response?.status === 503;
            if (shouldFallback) {
                toast.loading("Google Ads token not approved — retrying via DataForSEO…", {
                    id: t,
                });
                try {
                    const dfs = await keywordPlannerIdeasDFS(basePayload);
                    setResult({ ...dfs, source: "dataforseo", _sourceMeta: sourceMeta });
                    setSelection({});
                    setExcludedGroups(new Set());
                    toast.success(
                        `Got ${dfs.ideas?.length || 0} keyword ideas (via DataForSEO)`,
                        { id: t }
                    );
                    return;
                } catch (e2) {
                    const msg2 =
                        errText(e2, "DataForSEO fallback failed");
                    toast.error(msg2, { id: t });
                    return;
                }
            }
            const msg = detail || e.message || "Fetch failed";
            toast.error(msg, { id: t });
        } finally {
            setLoading(false);
        }
    };

    // ---- Derived: filtered + classified ideas ----
    const brandTermList = useMemo(
        () =>
            brandTerms
                .split(",")
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean),
        [brandTerms]
    );

    const brandGroupNames = useMemo(
        () =>
            new Set(
                (result?.concept_groups || [])
                    .filter((g) => (g.type || "").toUpperCase() === "BRAND")
                    .map((g) => g.name)
            ),
        [result]
    );

    const q = query.trim().toLowerCase();
    const groupsForIdea = (idea) => new Set((idea.concepts || []).map((c) => c.group));

    // Attribution: priority Related > Planner > Competitor. If no competitor
    // URL was used, "Competitor" is never assigned (everything falls back to
    // Planner). This matches how Google Ads blends ideas from seeds + URL
    // into one list — we just surface which bucket likely triggered the row.
    const sourceForIdea = useCallback(
        (idea) => {
            const meta = result?._sourceMeta;
            const kw = (idea.keyword || "").toLowerCase();
            if (!meta) return "Planner";
            const related = new Set(meta.relatedSeeds || []);
            const user = new Set(meta.userSeeds || []);
            if (related.has(kw)) return "Related";
            if (user.has(kw)) return "Planner";
            // Substring match for near-misses from the API (keyword variants).
            for (const r of related) if (r && kw.includes(r)) return "Related";
            for (const u of user) if (u && kw.includes(u)) return "Planner";
            return meta.hasCompetitor ? "Competitor" : "Planner";
        },
        [result]
    );

    const classified = useMemo(() => {
        if (!result?.ideas) return { branded: [], non_branded: [] };
        const branded = [];
        const non_branded = [];
        // Dedupe by lowercased keyword, keeping the row with the most data
        // (highest avg_monthly_searches; break ties on non-UNSPECIFIED
        // competition). The planner API already returns unique keywords,
        // but this makes the module safe if we ever merge multiple sources.
        const dedup = new Map();
        for (const idea of result.ideas) {
            const key = (idea.keyword || "").toLowerCase();
            if (!key) continue;
            const prev = dedup.get(key);
            if (!prev) {
                dedup.set(key, idea);
                continue;
            }
            const prevVol = prev.avg_monthly_searches || 0;
            const curVol = idea.avg_monthly_searches || 0;
            if (curVol > prevVol) {
                dedup.set(key, idea);
            } else if (
                curVol === prevVol &&
                (prev.competition || "").toUpperCase() === "UNSPECIFIED" &&
                (idea.competition || "").toUpperCase() !== "UNSPECIFIED"
            ) {
                dedup.set(key, idea);
            }
        }
        for (const idea of dedup.values()) {
            const ideaGroups = groupsForIdea(idea);
            let skip = false;
            for (const g of ideaGroups) {
                if (excludedGroups.has(g)) {
                    skip = true;
                    break;
                }
            }
            if (skip) continue;
            if (q && !idea.keyword.toLowerCase().includes(q)) continue;

            // Unified-table filters
            const src = sourceForIdea(idea);
            if (!sourceFilter.has(src)) continue;
            if ((idea.avg_monthly_searches || 0) < (minSearches || 0)) continue;
            const compKey = (idea.competition || "UNSPECIFIED").toUpperCase();
            if (!compFilter.has(compKey)) continue;

            const tagged = { ...idea, _source: src };
            if (isBrandedIdea(tagged, brandTermList, brandGroupNames)) {
                branded.push(tagged);
            } else {
                non_branded.push(tagged);
            }
        }
        return { branded, non_branded };
    }, [
        result,
        excludedGroups,
        brandTermList,
        brandGroupNames,
        q,
        sourceFilter,
        minSearches,
        compFilter,
        sourceForIdea,
    ]);

    const activeRows = tab === "branded" ? classified.branded : classified.non_branded;

    const sortedRows = useMemo(() => {
        const rows = [...activeRows];
        const dir = sortDir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            let av, bv;
            switch (sortBy) {
                case "keyword":
                    return a.keyword.localeCompare(b.keyword) * dir;
                case "competition":
                    av = a.competition_index || 0;
                    bv = b.competition_index || 0;
                    return (av - bv) * dir;
                case "high_bid":
                    av = a.high_top_page_bid || 0;
                    bv = b.high_top_page_bid || 0;
                    return (av - bv) * dir;
                default:
                    av = a.avg_monthly_searches || 0;
                    bv = b.avg_monthly_searches || 0;
                    return (av - bv) * dir;
            }
        });
        return rows;
    }, [activeRows, sortBy, sortDir]);

    // ---- Selection helpers ----
    const setPick = (keyword, kind) =>
        setSelection((prev) => {
            const next = { ...prev };
            if (kind === null || prev[keyword] === kind) {
                delete next[keyword];
            } else {
                next[keyword] = kind;
            }
            return next;
        });

    const bulkAssign = (kind) => {
        setSelection((prev) => {
            const next = { ...prev };
            for (const r of sortedRows) next[r.keyword] = kind;
            return next;
        });
        toast.success(
            `${sortedRows.length} keyword${sortedRows.length === 1 ? "" : "s"} marked as ${
                kind === "target" ? "targets" : "negatives"
            }`
        );
    };

    const bulkAssignBranded = (kind) => {
        setSelection((prev) => {
            const next = { ...prev };
            for (const r of classified.branded) next[r.keyword] = kind;
            return next;
        });
        toast.success(
            `${classified.branded.length} branded keyword${
                classified.branded.length === 1 ? "" : "s"
            } marked as ${kind === "target" ? "targets" : "negatives"}`
        );
    };

    const clearAllSelection = () => setSelection({});

    // "Export All" — the unified, filter-aware CSV of every keyword
    // currently visible across Non-Branded + Branded tabs, with the
    // Source column populated from the attribution logic.
    const unifiedExportRows = useMemo(() => {
        const combined = [
            ...classified.non_branded.map((r) => ({ ...r, _bucket: "Non-Branded" })),
            ...classified.branded.map((r) => ({ ...r, _bucket: "Branded" })),
        ];
        return combined.map((r) => ({
            keyword: r.keyword,
            source: r._source || "Planner",
            bucket: r._bucket,
            avg_monthly_searches: r.avg_monthly_searches || 0,
            competition: r.competition || "UNSPECIFIED",
            low_top_page_bid: r.low_top_page_bid ?? "",
            high_top_page_bid: r.high_top_page_bid ?? "",
        }));
    }, [classified]);

    const sourceCounts = useMemo(() => {
        const all = [...classified.non_branded, ...classified.branded];
        const counts = { Planner: 0, Related: 0, Competitor: 0 };
        for (const r of all) {
            const s = r._source || "Planner";
            counts[s] = (counts[s] || 0) + 1;
        }
        return counts;
    }, [classified]);

    const handleExportAll = useCallback(() => {
        if (!unifiedExportRows.length) {
            toast.error("No keywords in the current filtered view");
            return;
        }
        csvDownload("keyword-research-all.csv", unifiedExportRows);
        toast.success(
            `Exported ${unifiedExportRows.length} keyword${unifiedExportRows.length === 1 ? "" : "s"}`
        );
    }, [unifiedExportRows]);

    // Distiller — one-shot AI segmentation; merges results into `selection`.
    // Called per-bucket independently or globally. Payload keys correspond to
    // buckets — Target + Branded → `target`, Negatives → `negative`.
    const handleDistillerConfirm = ({ targets = [], branded = [], negatives = [] }) => {
        setSelection((prev) => {
            const next = { ...prev };
            targets.forEach((kw) => { next[kw] = "target"; });
            branded.forEach((kw) => { next[kw] = "target"; });
            negatives.forEach((kw) => { next[kw] = "negative"; });
            return next;
        });
    };

    const targetsList = useMemo(() => {
        if (!result?.ideas) return [];
        return result.ideas
            .filter((i) => selection[i.keyword] === "target")
            .map((i) => ({
                keyword: i.keyword,
                avg_monthly_searches: i.avg_monthly_searches,
                competition: i.competition,
                low_top_page_bid: i.low_top_page_bid,
                high_top_page_bid: i.high_top_page_bid,
            }));
    }, [result, selection]);

    const negativesList = useMemo(() => {
        if (!result?.ideas) return [];
        return result.ideas
            .filter((i) => selection[i.keyword] === "negative")
            .map((i) => ({
                keyword: i.keyword,
                avg_monthly_searches: i.avg_monthly_searches,
                competition: i.competition,
            }));
    }, [result, selection]);

    const toggleGroup = (groupName) =>
        setExcludedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupName)) next.delete(groupName);
            else next.add(groupName);
            return next;
        });

    const handlePushNegatives = async () => {
        if (!negativesList.length) {
            toast.error("Select some negatives first");
            return;
        }
        if (!customerId) {
            toast.error("Pick a client account first");
            return;
        }
        setPushingNegs(true);
        const t = toast.loading(
            `Pushing ${negativesList.length} negatives to a shared list…`
        );
        try {
            const data = await campaignBuilderPushNegatives({
                customer_id: customerId,
                list_name: "",
                negatives: negativesList.map((n) => n.keyword),
            });
            toast.success(
                `Created shared list "${data.list_name}" with ${data.count} negatives`,
                { id: t }
            );
        } catch (e) {
            const msg =
                errText(e, "Push negatives failed");
            toast.error(msg, { id: t });
        } finally {
            setPushingNegs(false);
        }
    };

    // Language mapping — mirrors KP_LANGUAGES in backend for use in the wizard.
    const languageId = useMemo(() => {
        const found = (options.languages || []).find((l) => l.code === language);
        return found?.id || "1000";
    }, [options, language]);

    const wizardCity = useMemo(() => {
        if (selectedGeos.length) return selectedGeos[0].name || selectedGeos[0].canonical_name;
        return "";
    }, [selectedGeos]);

    const wizardCityGeoId = useMemo(() => {
        if (selectedGeos.length) return selectedGeos[0].id;
        return "";
    }, [selectedGeos]);

    const accountName = useMemo(() => {
        const a = accounts.find((x) => x.id === customerId);
        return a ? `${a.name || "Unnamed"} · ${a.id}` : customerId;
    }, [accounts, customerId]);

    // ---- Connection banners ----
    if (!status) {
        return (
            <div className="p-8 text-neutral-500 text-sm">Loading Keyword Planner…</div>
        );
    }
    if (!status.configured) {
        return (
            <div
                data-testid="kp-not-configured"
                className="m-6 border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
            >
                <Warning size={16} weight="bold" className="mt-0.5 shrink-0" />
                Google Ads API is not configured on this instance. Ask your admin to add the
                GOOGLE_ADS_* keys in backend .env before using the Keyword Planner.
            </div>
        );
    }
    if (!status.connected) {
        return (
            <div
                data-testid="kp-not-connected"
                className="m-6 border border-klein bg-klein/5 px-4 py-3 text-sm text-ink flex items-start gap-2"
            >
                <Cloud size={16} weight="bold" className="mt-0.5 shrink-0 text-klein" />
                Connect your Google Ads account first — use the “Connect Google Ads” button
                in the sidebar.
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto thin-scroll bg-white" data-testid="kp-scroll-root">
            {/* Input bar */}
            <div className="border-b-2 border-ink px-6 py-5 space-y-4 bg-neutral-50">
                <div className="flex items-center gap-2">
                    <MagnifyingGlass size={18} weight="bold" className="text-klein" />
                    <h1 className="font-heading text-lg font-black uppercase tracking-tight text-ink">
                        Keyword Planner
                    </h1>
                    <span className="mono-label text-neutral-500">
                        · native Google Ads keyword ideas
                    </span>
                </div>
                <AdClipboardStrip />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                    {/* Seeds */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center justify-between mb-1 gap-2">
                            <label className="mono-label">Seed keywords</label>
                            <button
                                data-testid="kp-import-related"
                                type="button"
                                onClick={handleImportRelated}
                                disabled={serpImporting}
                                className="h-6 px-2 border border-klein bg-klein/5 hover:bg-klein hover:text-white text-klein font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 disabled:opacity-40"
                                title="Pull related searches from your recent SERP research runs for this seed + city"
                            >
                                {serpImporting ? (
                                    <ArrowsClockwise size={9} weight="bold" className="animate-spin" />
                                ) : (
                                    <ArrowFatDown size={9} weight="bold" />
                                )}
                                Import related ({serpImported.length})
                            </button>
                        </div>
                        <textarea
                            data-testid="kp-seeds"
                            value={seedText}
                            onChange={(e) => setSeedText(e.target.value)}
                            placeholder={"e.g. lg washing machine repair,\nsamsung fridge repair"}
                            rows={2}
                            className="w-full text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3 py-2 resize-none"
                        />
                    </div>
                    <div className="lg:col-span-4">
                        <label className="mono-label mb-1 block">Or landing page URL</label>
                        <input
                            data-testid="kp-url"
                            value={pageUrl}
                            onChange={(e) => setPageUrl(e.target.value)}
                            placeholder="https://client.com/services/washing-machine-repair"
                            className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                        />
                        <label className="mono-label mb-1 mt-2 block">
                            Brand terms (optional, comma separated)
                        </label>
                        <input
                            data-testid="kp-brand-terms"
                            value={brandTerms}
                            onChange={(e) => setBrandTerms(e.target.value)}
                            placeholder="metroberry, metro berry"
                            className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                        />
                    </div>
                    <div className="lg:col-span-3 space-y-2">
                        <div>
                            <label className="mono-label mb-1 block">Account</label>
                            <select
                                data-testid="kp-account"
                                value={customerId}
                                onChange={(e) => setCustomerId(e.target.value)}
                                disabled={accountsLoading || !accounts.length}
                                className="w-full h-9 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                            >
                                {accountsLoading ? (
                                    <option>Loading…</option>
                                ) : accounts.length === 0 ? (
                                    <option>No client accounts</option>
                                ) : (
                                    accounts.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {(a.name || "Unnamed")} · {a.id}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="mono-label mb-1 block">Country</label>
                                <select
                                    data-testid="kp-country"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    className="w-full h-9 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                                >
                                    {options.countries.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mono-label mb-1 block">Language</label>
                                <select
                                    data-testid="kp-language"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    className="w-full h-9 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                                >
                                    {options.languages.map((l) => (
                                        <option key={l.code} value={l.code}>
                                            {l.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* City / Region picker */}
                <div className="space-y-2">
                    <label className="mono-label block flex items-center gap-1">
                        <MapPin size={11} weight="bold" /> Cities / Regions (optional — narrows to the selected country)
                    </label>
                    <div className="relative">
                        <input
                            data-testid="kp-geo-query"
                            value={geoQuery}
                            onChange={(e) => setGeoQuery(e.target.value)}
                            onFocus={() => setGeoSuggestOpen(true)}
                            onBlur={() => setTimeout(() => setGeoSuggestOpen(false), 200)}
                            placeholder="Type a city, region or postcode — e.g. Bengaluru, Manchester, 90210"
                            className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                        />
                        {geoLoading && (
                            <ArrowsClockwise
                                size={13}
                                weight="bold"
                                className="absolute right-3 top-2.5 text-neutral-400 animate-spin"
                            />
                        )}
                        {geoSuggestOpen && geoSuggestions.length > 0 && (
                            <div
                                data-testid="kp-geo-suggestions"
                                className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink shadow-lg max-h-64 overflow-y-auto"
                            >
                                {geoSuggestions.map((g) => (
                                    <button
                                        key={g.id}
                                        data-testid={`kp-geo-suggestion-${g.id}`}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            addGeo(g);
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs font-mono hover:bg-klein hover:text-white flex items-center justify-between gap-3 border-b border-neutral-200 last:border-b-0"
                                    >
                                        <span className="truncate">{g.canonical_name || g.name}</span>
                                        <span className="text-[10px] uppercase tracking-widest shrink-0 opacity-70">
                                            {g.target_type}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedGeos.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {selectedGeos.map((g) => (
                                <span
                                    key={g.id}
                                    data-testid={`kp-geo-chip-${g.id}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono bg-klein/10 text-klein border border-klein"
                                >
                                    <MapPin size={10} weight="bold" />
                                    {g.canonical_name || g.name}
                                    <button
                                        onClick={() => removeGeo(g.id)}
                                        className="ml-1 hover:text-ink"
                                        aria-label={`Remove ${g.name}`}
                                    >
                                        <X size={10} weight="bold" />
                                    </button>
                                </span>
                            ))}
                            <button
                                data-testid="kp-geo-clear"
                                onClick={() => setSelectedGeos([])}
                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-neutral-500 hover:text-ink"
                            >
                                Clear all
                            </button>
                            <span className="text-[10px] font-mono text-neutral-400 self-center ml-1">
                                Country ({country}) ignored — using city/region reach instead
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1">
                        {NETWORKS.map((n) => (
                            <button
                                key={n.key}
                                data-testid={`kp-network-${n.key}`}
                                onClick={() => setNetwork(n.key)}
                                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold border transition ${
                                    network === n.key
                                        ? "bg-ink text-white border-ink"
                                        : "bg-white text-ink border-neutral-300 hover:border-ink"
                                }`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </div>
                    <button
                        data-testid="kp-fetch"
                        onClick={handleFetch}
                        disabled={loading || !customerId}
                        className="brutal-btn border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[11px] font-bold px-4 h-9 flex items-center gap-2 disabled:opacity-40"
                    >
                        {loading ? (
                            <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                        ) : (
                            <Sparkle size={13} weight="bold" />
                        )}
                        Get keyword ideas
                    </button>
                    {result && (
                        <span className="mono-label text-neutral-500 flex items-center gap-2">
                            {result.ideas?.length || 0} ideas ·{" "}
                            {result.concept_groups?.length || 0} refine groups
                            {result.source === "dataforseo" && (
                                <span
                                    data-testid="kp-source-badge"
                                    className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold bg-amber-100 text-amber-900 border border-amber-300"
                                    title="Google Ads developer token pending approval — data pulled from DataForSEO"
                                >
                                    via DataForSEO
                                </span>
                            )}
                        </span>
                    )}
                </div>
            </div>

            {/* Body */}
            {!result ? (
                <EmptyPlanner loading={loading} />
            ) : (
                <div className="flex flex-1">
                    <RefinePanel
                        groups={result.concept_groups || []}
                        excludedGroups={excludedGroups}
                        onToggle={toggleGroup}
                        onReset={() => setExcludedGroups(new Set())}
                    />
                    <div className="flex-1 flex flex-col min-w-0">
                        <SelectionBar
                            targetsCount={targetsList.length}
                            negativesCount={negativesList.length}
                            onExportTargets={() =>
                                csvDownload(
                                    "keyword-planner-targets.csv",
                                    targetsList
                                )
                            }
                            onExportNegatives={() =>
                                csvDownload(
                                    "keyword-planner-negatives.csv",
                                    negativesList
                                )
                            }
                            onClearAll={clearAllSelection}
                            onMarkAllBrandedNeg={() => bulkAssignBranded("negative")}
                            brandedCount={classified.branded.length}
                            onPushCampaign={() => setWizardOpen(true)}
                            onPushNegatives={handlePushNegatives}
                            pushingNegs={pushingNegs}
                            ideasCount={result.ideas?.length || 0}
                            onOpenDistiller={() => setDistillerOpen(true)}
                            onExportAll={handleExportAll}
                            exportAllCount={unifiedExportRows.length}
                        />

                        <div className="border-b border-neutral-300 bg-neutral-50 flex items-center flex-wrap gap-2 px-4">
                            <TabBtn
                                testId="kp-tab-non-branded"
                                active={tab === "non_branded"}
                                onClick={() => setTab("non_branded")}
                                label="Non-Branded"
                                count={classified.non_branded.length}
                            />
                            <TabBtn
                                testId="kp-tab-branded"
                                active={tab === "branded"}
                                onClick={() => setTab("branded")}
                                label="Branded"
                                count={classified.branded.length}
                            />
                            <div className="flex-1" />
                            <input
                                data-testid="kp-search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filter keywords…"
                                className="h-7 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2 w-52"
                            />
                            <button
                                data-testid="kp-bulk-target"
                                onClick={() => bulkAssign("target")}
                                className="brutal-btn h-7 px-2 border border-emerald-700 bg-emerald-50 text-emerald-900 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                            >
                                <TargetIcon size={11} weight="bold" /> All → Target
                            </button>
                            <button
                                data-testid="kp-bulk-negative"
                                onClick={() => bulkAssign("negative")}
                                className="brutal-btn h-7 px-2 border border-red-700 bg-red-50 text-red-900 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                            >
                                <Prohibit size={11} weight="bold" /> All → Negative
                            </button>
                        </div>

                        <SourceFilterBar
                            sourceFilter={sourceFilter}
                            setSourceFilter={setSourceFilter}
                            minSearches={minSearches}
                            setMinSearches={setMinSearches}
                            compFilter={compFilter}
                            setCompFilter={setCompFilter}
                            sourceCounts={sourceCounts}
                        />

                        <div className="flex-1">
                            <ResultsTable
                                rows={sortedRows}
                                selection={selection}
                                onPick={setPick}
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={(col) => {
                                    if (sortBy === col) {
                                        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                                    } else {
                                        setSortBy(col);
                                        setSortDir(col === "keyword" ? "asc" : "desc");
                                    }
                                }}
                                brandTerms={brandTermList}
                                brandGroupNames={brandGroupNames}
                            />

                            <div className="p-3 bg-neutral-100 border-t-2 border-ink">
                                <KeywordThemer
                                    keywords={[...classified.non_branded, ...classified.branded]}
                                    defaultCampaignName={
                                        accountName
                                            ? `${accountName} — Keyword Research`
                                            : "Keyword Research Plan"
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <CampaignPushWizard
                open={wizardOpen}
                onOpenChange={(v) => {
                    setWizardOpen(v);
                    if (!v) setWizardOverride(null);
                }}
                targets={wizardOverride?.targets ?? targetsList}
                negatives={wizardOverride?.negatives ?? negativesList}
                country={country}
                language={language}
                city={wizardCity}
                cityGeoId={wizardCityGeoId}
                languageId={languageId}
                customerId={customerId}
                accountName={accountName}
            />
            <KeywordDistillerDialog
                open={distillerOpen}
                onOpenChange={(v) => {
                    setDistillerOpen(v);
                    // Clear the preload after the dialog closes so a fresh
                    // open kicks off a normal AI run again.
                    if (!v) setPreloadedDistiller(null);
                }}
                ideas={result?.ideas || []}
                seeds={result?.seeds || []}
                seedText={seedText}
                serpImported={serpImported}
                initialBrandText={brandTerms}
                country={country}
                city={wizardCity}
                customerId={customerId}
                onConfirm={handleDistillerConfirm}
                preloaded={preloadedDistiller}
                onAuditSaved={onAuditSaved}
                onBuildCampaign={({ targets = [], branded = [], negatives = [] }) => {
                    // Also merge into selection (so the SelectionBar stays in
                    // sync if this run has fresh planner ideas), then push
                    // the picked keywords directly into the wizard via an
                    // override — this works even when opening from Search
                    // History where result.ideas is empty.
                    handleDistillerConfirm({ targets, branded, negatives });
                    setWizardOverride({
                        targets: [...targets, ...branded].map((kw) => ({ keyword: kw })),
                        negatives: negatives.map((kw) => ({ keyword: kw })),
                    });
                    setDistillerOpen(false);
                    setPreloadedDistiller(null);
                    setTimeout(() => setWizardOpen(true), 60);
                }}
            />
        </div>
    );
};

// ----- Sub-components -----

const EmptyPlanner = ({ loading }) => (
    <div
        data-testid="kp-empty"
        className="flex-1 flex items-center justify-center p-10 text-center"
    >
        <div className="max-w-lg">
            <div className="mono-label mb-3">// keyword planner</div>
            <h2 className="font-display text-3xl font-black tracking-tighter text-ink leading-tight">
                Native keyword ideas <br />
                <span className="text-klein">straight from Google Ads.</span>
            </h2>
            <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                Enter seed keywords or a landing page URL. We pull Search volume, competition,
                and top-of-page bids from the connected Google Ads account, then use Google&apos;s
                own concept groups so you can refine by Brand, Product, or Service — and mark
                branded terms as negatives in a single click.
            </p>
            {loading && (
                <div className="mt-6 flex justify-center gap-1">
                    {[0, 1, 2, 3].map((i) => (
                        <span
                            key={i}
                            className="inline-block w-1.5 h-6 bg-klein"
                            style={{
                                animation: `bar-slide 1s ease-in-out ${i * 0.12}s infinite alternate`,
                                opacity: 0.6,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    </div>
);

const RefinePanel = ({ groups, excludedGroups, onToggle, onReset }) => {
    const [open, setOpen] = useState({});
    useEffect(() => {
        // Auto-open first 2 groups (usually Brand + Product)
        const init = {};
        groups.slice(0, 2).forEach((g) => (init[g.name] = true));
        setOpen(init);
    }, [groups]);

    if (!groups.length) {
        return (
            <aside className="w-[260px] border-r border-neutral-300 bg-white p-4 text-[11px] text-neutral-500 flex items-start gap-2">
                <Info size={14} className="mt-0.5" />
                <span>No concept groups returned for this seed. Try broader seeds.</span>
            </aside>
        );
    }

    return (
        <aside
            data-testid="kp-refine-panel"
            className="w-[260px] border-r border-neutral-300 bg-white overflow-y-auto thin-scroll"
        >
            <div className="flex items-center justify-between p-3 border-b border-neutral-200">
                <div className="flex items-center gap-2">
                    <Sliders size={14} weight="bold" className="text-klein" />
                    <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                        Refine keywords
                    </span>
                </div>
                {excludedGroups.size > 0 && (
                    <button
                        data-testid="kp-refine-reset"
                        onClick={onReset}
                        className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 hover:text-ink flex items-center gap-1"
                    >
                        <X size={10} /> Reset
                    </button>
                )}
            </div>
            <div className="divide-y divide-neutral-200">
                {groups.map((g) => {
                    const excluded = excludedGroups.has(g.name);
                    const isBrand = (g.type || "").toUpperCase() === "BRAND";
                    const isOpen = !!open[g.name];
                    return (
                        <div key={g.name} className="p-3">
                            <div className="flex items-center gap-2">
                                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                    <input
                                        data-testid={`kp-group-${g.name}`}
                                        type="checkbox"
                                        checked={!excluded}
                                        onChange={() => onToggle(g.name)}
                                        className="accent-klein"
                                    />
                                    <span className="text-sm font-bold text-ink truncate">
                                        {g.name}
                                    </span>
                                    {isBrand && (
                                        <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                            Brand
                                        </span>
                                    )}
                                </label>
                                <button
                                    onClick={() =>
                                        setOpen((prev) => ({ ...prev, [g.name]: !prev[g.name] }))
                                    }
                                    className="text-neutral-500 hover:text-ink"
                                    aria-label="Toggle group"
                                >
                                    {isOpen ? (
                                        <CaretDown size={12} weight="bold" />
                                    ) : (
                                        <CaretRight size={12} weight="bold" />
                                    )}
                                </button>
                            </div>
                            {isOpen && g.concepts?.length > 0 && (
                                <div className="mt-2 pl-6 flex flex-wrap gap-1">
                                    {g.concepts.slice(0, 18).map((c) => (
                                        <span
                                            key={c}
                                            className="px-1.5 py-0.5 text-[10px] font-mono bg-neutral-100 text-neutral-700 border border-neutral-200"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                    {g.concepts.length > 18 && (
                                        <span className="text-[10px] font-mono text-neutral-400">
                                            +{g.concepts.length - 18}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
};

const SOURCE_STYLES = {
    Planner: "bg-klein/10 text-klein border-klein/40",
    Related: "bg-amber-100 text-amber-900 border-amber-300",
    Competitor: "bg-violet-100 text-violet-900 border-violet-300",
};

const SourceFilterBar = ({
    sourceFilter,
    setSourceFilter,
    minSearches,
    setMinSearches,
    compFilter,
    setCompFilter,
    sourceCounts,
}) => {
    const toggle = (setter, current, key) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setter(next);
    };
    const Chip = ({ active, onClick, children, testId, cls = "" }) => (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`h-6 px-2 border font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 transition ${
                active
                    ? `${cls || "border-ink bg-ink text-white"}`
                    : "border-neutral-300 bg-white text-neutral-500 hover:text-ink"
            }`}
        >
            {children}
        </button>
    );
    return (
        <div
            data-testid="kp-source-filter-bar"
            className="border-b border-neutral-300 bg-white px-4 py-2 flex items-center flex-wrap gap-3 text-[11px] font-mono text-neutral-600"
        >
            <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                Source:
            </span>
            {["Planner", "Related", "Competitor"].map((s) => (
                <Chip
                    key={s}
                    testId={`kp-source-${s.toLowerCase()}`}
                    active={sourceFilter.has(s)}
                    onClick={() => toggle(setSourceFilter, sourceFilter, s)}
                    cls={
                        sourceFilter.has(s)
                            ? `${SOURCE_STYLES[s]} border`
                            : ""
                    }
                >
                    {s} <span className="opacity-60">({sourceCounts?.[s] || 0})</span>
                </Chip>
            ))}
            <span className="mx-2 h-4 w-px bg-neutral-300" />
            <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                Min searches:
            </span>
            <input
                data-testid="kp-min-searches"
                type="number"
                min="0"
                step="100"
                value={minSearches}
                onChange={(e) => setMinSearches(Number(e.target.value) || 0)}
                className="h-6 w-20 border border-neutral-300 focus:border-ink focus:outline-none px-2 text-[11px] font-mono"
            />
            <span className="mx-2 h-4 w-px bg-neutral-300" />
            <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                Competition:
            </span>
            {["LOW", "MEDIUM", "HIGH", "UNSPECIFIED"].map((c) => (
                <Chip
                    key={c}
                    testId={`kp-comp-${c.toLowerCase()}`}
                    active={compFilter.has(c)}
                    onClick={() => toggle(setCompFilter, compFilter, c)}
                    cls={
                        compFilter.has(c)
                            ? c === "LOW"
                                ? "border-emerald-700 bg-emerald-50 text-emerald-900"
                                : c === "MEDIUM"
                                ? "border-amber-700 bg-amber-50 text-amber-900"
                                : c === "HIGH"
                                ? "border-red-700 bg-red-50 text-red-900"
                                : "border-neutral-500 bg-neutral-100 text-neutral-700"
                            : ""
                    }
                >
                    {c}
                </Chip>
            ))}
        </div>
    );
};

const SelectionBar = ({
    targetsCount,
    negativesCount,
    onExportTargets,
    onExportNegatives,
    onClearAll,
    onMarkAllBrandedNeg,
    brandedCount,
    onPushCampaign,
    onPushNegatives,
    pushingNegs,
    ideasCount,
    onOpenDistiller,
    onExportAll,
    exportAllCount,
}) => (
    <div
        data-testid="kp-selection-bar"
        className="border-b border-neutral-300 bg-ink text-white px-4 py-2 flex items-center flex-wrap gap-3"
    >
        <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-widest font-bold">
            <span data-testid="kp-count-targets" className="flex items-center gap-1.5">
                <TargetIcon size={12} weight="bold" className="text-emerald-400" />
                {targetsCount} targets
            </span>
            <span data-testid="kp-count-negatives" className="flex items-center gap-1.5">
                <Prohibit size={12} weight="bold" className="text-red-400" />
                {negativesCount} negatives
            </span>
        </div>
        <div className="flex-1" />
        <button
            data-testid="kp-open-distiller"
            onClick={onOpenDistiller}
            disabled={!ideasCount}
            className="brutal-btn h-7 px-2 border border-yellow-400 bg-yellow-400 text-ink hover:bg-yellow-300 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
            title="AI segments every keyword into Target · Branded · Negatives"
        >
            <Sparkle size={11} weight="bold" /> AI Distill ({ideasCount})
        </button>
        <button
            data-testid="kp-push-campaign"
            onClick={onPushCampaign}
            disabled={targetsCount === 0}
            className="brutal-btn h-7 px-2 border border-klein bg-klein text-white hover:bg-klein/80 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
            title="Enrich, cluster into ad groups, and push a paused campaign"
        >
            <RocketLaunch size={11} weight="bold" /> Push to Google Ads
        </button>
        <button
            data-testid="kp-push-negatives"
            onClick={onPushNegatives}
            disabled={negativesCount === 0 || pushingNegs}
            className="brutal-btn h-7 px-2 border border-red-400 bg-transparent text-red-300 hover:bg-red-500 hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
            title="Push these negatives to a shared negative list in the account"
        >
            {pushingNegs ? (
                <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
            ) : (
                <UploadSimple size={11} weight="bold" />
            )}
            Push negatives to Ads
        </button>
        <button
            data-testid="kp-mark-branded-neg"
            onClick={onMarkAllBrandedNeg}
            disabled={brandedCount === 0}
            className="brutal-btn h-7 px-2 border border-red-400 bg-transparent text-red-300 hover:bg-red-500 hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
            title="Mark all branded keywords as negatives"
        >
            <Prohibit size={11} weight="bold" /> All branded → Negatives ({brandedCount})
        </button>
        <button
            data-testid="kp-export-all"
            onClick={onExportAll}
            disabled={!exportAllCount}
            className="brutal-btn h-7 px-2 border border-white bg-white text-ink hover:bg-neutral-100 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
            title="Export every keyword in the current filtered view (Planner + Related + Competitor) with its Source column"
        >
            <Download size={11} weight="bold" /> Export All ({exportAllCount || 0})
        </button>
        <button
            data-testid="kp-export-targets"
            onClick={onExportTargets}
            disabled={targetsCount === 0}
            className="brutal-btn h-7 px-2 border border-emerald-400 bg-transparent text-emerald-300 hover:bg-emerald-500 hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
        >
            <Download size={11} weight="bold" /> Targets CSV
        </button>
        <button
            data-testid="kp-export-negatives"
            onClick={onExportNegatives}
            disabled={negativesCount === 0}
            className="brutal-btn h-7 px-2 border border-red-400 bg-transparent text-red-300 hover:bg-red-500 hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
        >
            <Download size={11} weight="bold" /> Negatives CSV
        </button>
        <button
            data-testid="kp-clear"
            onClick={onClearAll}
            disabled={targetsCount === 0 && negativesCount === 0}
            className="brutal-btn h-7 px-2 border border-neutral-500 bg-transparent text-neutral-300 hover:bg-neutral-700 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
        >
            <Trash size={11} weight="bold" /> Clear
        </button>
    </div>
);

const TabBtn = ({ active, onClick, label, count, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className={`px-3 py-2 font-mono uppercase tracking-widest text-[10px] font-bold border-b-2 transition ${
            active
                ? "border-b-klein text-ink"
                : "border-transparent text-neutral-500 hover:text-ink"
        }`}
    >
        {label} <span className="text-neutral-400">({count})</span>
    </button>
);

const SortHeader = ({ label, col, sortBy, sortDir, onSort, align = "left" }) => {
    const active = sortBy === col;
    return (
        <th
            className={`px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold cursor-pointer select-none whitespace-nowrap ${
                align === "right" ? "text-right" : "text-left"
            }`}
            onClick={() => onSort(col)}
        >
            {label}
            {active && (
                <span className="ml-1 text-klein">
                    {sortDir === "asc" ? "▲" : "▼"}
                </span>
            )}
        </th>
    );
};

const compClass = (c) => {
    const v = (c || "").toLowerCase();
    if (v === "high") return "bg-red-100 text-red-900 border-red-300";
    if (v === "medium") return "bg-amber-100 text-amber-900 border-amber-300";
    if (v === "low") return "bg-emerald-100 text-emerald-900 border-emerald-300";
    return "bg-neutral-100 text-neutral-700 border-neutral-300";
};

const ResultsTable = ({
    rows,
    selection,
    onPick,
    sortBy,
    sortDir,
    onSort,
    brandTerms,
    brandGroupNames,
}) => {
    if (!rows.length) {
        return (
            <div className="flex-1 flex items-center justify-center text-sm text-neutral-500">
                No keyword ideas match this view.
            </div>
        );
    }
    return (
        <div className="w-full">
            <table className="w-full text-sm">
                <thead className="bg-neutral-100 sticky top-0 z-[1]">
                    <tr>
                        <th className="px-3 py-2 w-6" />
                        <SortHeader
                            label="Keyword"
                            col="keyword"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                        />
                        <th className="px-3 py-2 text-left font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold whitespace-nowrap">
                            Source
                        </th>
                        <SortHeader
                            label="Avg Searches"
                            col="searches"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                            align="right"
                        />
                        <SortHeader
                            label="Competition"
                            col="competition"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                            align="right"
                        />
                        <SortHeader
                            label="Low Bid"
                            col="low_bid"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                            align="right"
                        />
                        <SortHeader
                            label="High Bid"
                            col="high_bid"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                            align="right"
                        />
                        <th className="px-3 py-2 text-right font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold w-[220px]">
                            Action
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const branded =
                            brandTerms.some((b) =>
                                (r.keyword || "").toLowerCase().includes(b)
                            ) ||
                            (r.concepts || []).some(
                                (c) =>
                                    (c.type || "").toUpperCase() === "BRAND" ||
                                    brandGroupNames.has(c.group)
                            );
                        const pick = selection[r.keyword];
                        return (
                            <tr
                                key={r.keyword}
                                className={`border-t border-neutral-200 hover:bg-neutral-50 ${
                                    pick === "target"
                                        ? "bg-emerald-50/50"
                                        : pick === "negative"
                                        ? "bg-red-50/50"
                                        : ""
                                }`}
                                data-testid={`kp-row-${r.keyword}`}
                            >
                                <td className="px-3 py-2 align-middle">
                                    <input
                                        type="checkbox"
                                        checked={!!pick}
                                        onChange={() =>
                                            pick ? onPick(r.keyword, null) : onPick(r.keyword, "target")
                                        }
                                        className="accent-klein"
                                    />
                                </td>
                                <td className="px-3 py-2 font-medium text-ink">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span>{r.keyword}</span>
                                        {branded && (
                                            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                                Brand
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <span
                                        data-testid={`kp-source-cell-${r.keyword}`}
                                        className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold border ${
                                            SOURCE_STYLES[r._source || "Planner"] || SOURCE_STYLES.Planner
                                        }`}
                                    >
                                        {r._source || "Planner"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-ink">
                                    {(r.avg_monthly_searches || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <span
                                        className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold border ${compClass(
                                            r.competition
                                        )}`}
                                    >
                                        {r.competition || "—"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-ink">
                                    {r.low_top_page_bid
                                        ? r.low_top_page_bid.toFixed(2)
                                        : "—"}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-ink">
                                    {r.high_top_page_bid
                                        ? r.high_top_page_bid.toFixed(2)
                                        : "—"}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="inline-flex">
                                        <button
                                            data-testid={`kp-target-${r.keyword}`}
                                            onClick={() =>
                                                onPick(
                                                    r.keyword,
                                                    pick === "target" ? null : "target"
                                                )
                                            }
                                            className={`h-7 px-2 border font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 transition ${
                                                pick === "target"
                                                    ? "border-emerald-700 bg-emerald-700 text-white"
                                                    : "border-emerald-700 text-emerald-900 bg-white hover:bg-emerald-50"
                                            }`}
                                        >
                                            <TargetIcon size={10} weight="bold" /> Target
                                        </button>
                                        <button
                                            data-testid={`kp-negative-${r.keyword}`}
                                            onClick={() =>
                                                onPick(
                                                    r.keyword,
                                                    pick === "negative" ? null : "negative"
                                                )
                                            }
                                            className={`h-7 px-2 border-l-0 border font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 transition ${
                                                pick === "negative"
                                                    ? "border-red-700 bg-red-700 text-white"
                                                    : "border-red-700 text-red-900 bg-white hover:bg-red-50"
                                            }`}
                                        >
                                            <Prohibit size={10} weight="bold" /> Negative
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
